import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaInstagram, FaWhatsapp } from 'react-icons/fa';
import {
    LuArrowRight,
    LuBot,
    LuCheck,
    LuChevronRight,
    LuMonitor,
    LuPackage,
    LuPlug,
    LuQrCode,
    LuRefreshCw,
    LuTriangleAlert,
    LuUserPlus,
    LuX,
} from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import CenaBoasVindas from '../components/boasvindas/CenaBoasVindas';
import ConectarMetaPanel from '../components/conexoes/ConectarMetaPanel';
import { destinoDentroDoApp, marcarBoasVindasNoNavegador } from '../config/boasVindas';
import { ehAdministradorDaEmpresa, temFuncionalidade } from '../config/permissions';
import { useCelular } from '../hooks/useCelular';
import { useConectarInstagram } from '../hooks/useConectarInstagram';
import { useConectarQr } from '../hooks/useConectarQr';
import { useConectarWhatsappMeta } from '../hooks/useConectarWhatsappMeta';
import {
    ConexaoPlataformaService,
    PlataformaEnum,
    StatusConexaoEnum,
    type ConexaoPlataforma,
} from '../services/ConexaoPlataformaService';
import { MetaService, type MetaConfig, type StatusWhatsapp } from '../services/MetaService';
import { NotificacaoService } from '../services/NotificacaoService';
/* Os painéis de conectar (o "O que você vai precisar" da Meta) vestem as classes `cx-`. */
import './ConnectionsPage.css';
import './BemVindoPage.css';

/* ─────────────────────────────────────────────────────────────────────────
   BOAS-VINDAS — /bem-vindo, o primeiro acesso guiado.
   Desenho fechado em 28/10/2026: docs/propostas/entrada/boas-vindas.html

   Três passos, pouco texto, uma saída em cada: o Brik AI se apresenta e pede
   um canal; a pessoa conecta o WhatsApp (API oficial ou QR Code) ou o
   Instagram aqui mesmo; e chega na Home com a conta viva.

   Quem vê é a API quem diz (`boasVindas` no Auth/Me): só o administrador que
   criou a conta, em empresa criada depois do lançamento, enquanto não houver a
   marca. Quem já usa o sistema e quem é convidado nunca caem aqui. A marca
   ("concluido" / "pulado-N") vai para a mesma tabela da apresentação do Brik,
   e a apresentação é marcada junto: o passo 1 É a apresentação, ajustada.

   Conectar reaproveita o que Conexões e o Chat sem número já usam: a janela
   da Meta (`useConectarWhatsappMeta`), o ciclo do QR Code (`useConectarQr`,
   mostrado dentro do bloco) e o login do Instagram (`useConectarInstagram`).
   ───────────────────────────────────────────────────────────────────────── */

type Passo = 1 | 2 | 3;

/* Os canais de conversa. Lead Ads, RD e webhook não contam: o guia é sobre a primeira conversa. */
const CANAIS_DE_CONVERSA = new Set<number>([
    PlataformaEnum.WhatsappMeta,
    PlataformaEnum.WhatsappEvolution,
    PlataformaEnum.Instagram,
]);

/* O nome da primeira conexão via QR Code. O guia não pede nome: é a primeira da empresa. */
const NOME_DA_CONEXAO_QR = 'WhatsApp';

const primeiroNome = (nome?: string) => (nome ?? '').trim().split(/\s+/)[0] || '';

const lerUsuario = (): { nome?: string } | null => {
    try {
        return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
        return null;
    }
};

/** "5541999990001" → "(41) 99999-0001"; o que não for número brasileiro volta como veio. */
const numeroLegivel = (identificador: string): string => {
    const d = identificador.replace(/\D/g, '');
    const semDdi = d.startsWith('55') && (d.length === 12 || d.length === 13) ? d.slice(2) : d;
    if (semDdi.length === 11) return `(${semDdi.slice(0, 2)}) ${semDdi.slice(2, 7)}-${semDdi.slice(7)}`;
    if (semDdi.length === 10) return `(${semDdi.slice(0, 2)}) ${semDdi.slice(2, 6)}-${semDdi.slice(6)}`;
    return identificador;
};

const rotuloDoCanal = (c: ConexaoPlataforma): string =>
    c.plataforma === PlataformaEnum.Instagram
        ? `Instagram · ${c.identificador.startsWith('@') ? '' : '@'}${c.identificador}`
        : `WhatsApp · ${numeroLegivel(c.identificador)}`;

const apoioDoCanal = (c: ConexaoPlataforma): string =>
    c.plataforma === PlataformaEnum.WhatsappMeta
        ? 'API oficial · as conversas já chegam no Chat'
        : c.plataforma === PlataformaEnum.WhatsappEvolution
            ? 'via QR Code · as conversas já chegam no Chat'
            : 'as mensagens diretas já chegam no Chat';

const BemVindoPage = () => {
    const navigate = useNavigate();
    const celular = useCelular();
    const usuario = useMemo(lerUsuario, []);
    const nome = primeiroNome(usuario?.nome);
    const admin = ehAdministradorDaEmpresa();
    /* As formas de conectar seguem as chaves de Sistema › Funcionalidades, como em
       Conexões. O QR Code não aparece no celular: ninguém lê um código na própria tela. */
    const qrLigado = temFuncionalidade('whatsapp_evolution');
    const mostrarQr = qrLigado && !celular;
    const mostrarInstagram = temFuncionalidade('instagram');

    const [passo, setPasso] = useState<Passo>(1);
    const [conexoes, setConexoes] = useState<ConexaoPlataforma[]>([]);
    const [modoQr, setModoQr] = useState(false);
    const [painelMeta, setPainelMeta] = useState(false);
    const [metaConfig, setMetaConfig] = useState<MetaConfig | null>(null);
    const [statusWa, setStatusWa] = useState<StatusWhatsapp | null>(null);
    const [saindo, setSaindo] = useState(false);

    /* Sem sessão não há guia: a tela vive fora do app e cuida disso sozinha. */
    useEffect(() => {
        if (!usuario) navigate('/login', { replace: true });
    }, [usuario, navigate]);

    const recarregar = useCallback(async () => {
        try {
            const r = await ConexaoPlataformaService.buscarConexoesAtivas();
            if (r.sucesso) {
                setConexoes((r.dados ?? []).filter(
                    c => c.status === StatusConexaoEnum.Ativa && CANAIS_DE_CONVERSA.has(c.plataforma),
                ));
            }
        } catch { /* a lista fica como está; o passo 3 fala no condicional */ }
    }, []);

    useEffect(() => { void recarregar(); }, [recarregar]);

    useEffect(() => {
        if (!admin) return;
        MetaService.getMetaConfig().then(r => { if (r.sucesso && r.dados) setMetaConfig(r.dados); }).catch(() => null);
        MetaService.getWhatsappStatus().then(r => { if (r?.sucesso) setStatusWa(r.dados); }).catch(() => null);
    }, [admin]);

    /* ── as três formas de conectar ── */
    const { pronto: metaPronto, conectando: conectandoMeta, conectar: conectarMeta } = useConectarWhatsappMeta({
        appId: metaConfig?.appId,
        configurationId: metaConfig?.configurationId,
        aoConectar: async () => { setPainelMeta(false); await recarregar(); },
    });
    const { conectando: conectandoIg, abrir: abrirInstagram } = useConectarInstagram({ aoConectar: recarregar });
    const qr = useConectarQr({
        ativo: modoQr,
        onConectado: () => { void recarregar(); setModoQr(false); },
    });

    /* Ao abrir o QR Code, a conexão é criada sozinha, uma vez por abertura; se
       falhar, a tela mostra o motivo e o "Tentar de novo" cria outra. */
    const pediuCriar = useRef(false);
    const { fase: faseQr, criar: criarQr } = qr;
    useEffect(() => {
        if (!modoQr) { pediuCriar.current = false; return; }
        if (faseQr === 'nome' && !pediuCriar.current) {
            pediuCriar.current = true;
            void criarQr(NOME_DA_CONEXAO_QR);
        }
    }, [modoQr, faseQr, criarQr]);

    /* O limite do plano para o WhatsApp (Meta e QR Code somados), como em Conexões. */
    const limiteWa = statusWa?.limiteWhatsapp ?? statusWa?.limiteConexaoPorPlataforma ?? null;
    const usadasWa = statusWa?.whatsappsConectados
        ?? conexoes.filter(c => c.plataforma !== PlataformaEnum.Instagram).length;
    const podeWa = statusWa?.podeConectarWhatsapp ?? (limiteWa === null || usadasWa < limiteWa);
    const bloqueioWa = podeWa ? null : (statusWa?.motivoBloqueioWhatsapp || 'Limite do plano atingido');

    /* ── a marca, e a saída ── */
    const gravar = async (chave: 'concluido' | `pulado-${Passo}`) => {
        try {
            /* Quem passa pelo guia não vê a apresentação do Brik na Home: o passo 1 é ela. */
            await Promise.all([
                NotificacaoService.marcarBoasVindas(chave),
                NotificacaoService.marcarApresentacaoVista(),
            ]);
        } catch { /* sem a marca no servidor, o próximo login volta aqui — melhor que travar a saída */ }
        marcarBoasVindasNoNavegador(chave === 'concluido' ? 'concluido' : 'pulado');
    };

    const concluir = async (destino: string = destinoDentroDoApp()) => {
        if (saindo) return;
        setSaindo(true);
        await gravar('concluido');
        navigate(destino, { replace: true });
    };

    const pular = async () => {
        if (saindo) return;
        setSaindo(true);
        await gravar(`pulado-${passo}`);
        navigate(destinoDentroDoApp(), { replace: true });
    };

    if (!usuario) return null;

    const temCanal = conexoes.length > 0;
    /* Decisão dele em 28/10, ao testar: com o WhatsApp conectado, as duas formas do WhatsApp
       somem e fica só o Instagram (e vice-versa). O guia é para o primeiro canal; mais um
       número se conecta em Conexões. */
    const temWhatsapp = conexoes.some(c => c.plataforma !== PlataformaEnum.Instagram);
    const temInstagram = conexoes.some(c => c.plataforma === PlataformaEnum.Instagram);
    const erroQr = qr.fase === 'erro' || qr.fase === 'recriar' || qr.fase === 'expirado' || (qr.fase === 'nome' && !!qr.msg);

    const linhaDepois = (icone: ReactNode, titulo: string, apoio: string, rota: string) => (
        <button type="button" className="bv-op is-link" onClick={() => concluir(rota)} disabled={saindo}>
            <span className="bv-bx">{icone}</span>
            <span className="bv-tx">
                <span className="bv-nm">{titulo}</span>
                <span className="bv-ap">{apoio}</span>
            </span>
            <LuChevronRight size={16} className="bv-chev" />
        </button>
    );

    return (
        <AuthShell largo>
            <div className="bv">
                {passo !== 3 && (
                    <button type="button" className="bv-x" aria-label="Sair do guia" title="Sair do guia" onClick={pular} disabled={saindo}>
                        <LuX size={16} />
                    </button>
                )}

                <div className="bv-prog" role="status" aria-label={`Passo ${passo} de 3`}>
                    <span>Passo {passo} de 3</span>
                    <div className="bv-seg">
                        {([1, 2, 3] as Passo[]).map(n => <i key={n} className={n <= passo ? 'is-on' : ''} />)}
                    </div>
                </div>

                {/* ─── 1 · Oi, eu sou o Brik AI ─────────────────────────── */}
                {passo === 1 && (
                    <>
                        <CenaBoasVindas passo={1} />
                        <h1 className="bv-h1">Oi{nome ? `, ${nome}` : ''}. Eu sou o Brik AI.</h1>
                        <p className="bv-sub">Conecte o WhatsApp ou o Instagram e as conversas da sua empresa passam a chegar aqui.</p>
                        <div className="bv-acoes">
                            <button type="button" className="bv-btn" autoFocus onClick={() => setPasso(2)}>
                                Começar <LuArrowRight size={15} />
                            </button>
                            <button type="button" className="bv-lk" onClick={pular} disabled={saindo}>Ver o sistema primeiro</button>
                        </div>
                    </>
                )}

                {/* ─── 2 · Conecte um canal ─────────────────────────────── */}
                {passo === 2 && !modoQr && (
                    <>
                        <CenaBoasVindas passo={2} />
                        <h1 className="bv-h1">Conecte um canal</h1>
                        <p className="bv-sub">É por aqui que as conversas chegam.</p>

                        <div className="bv-ops">
                            {conexoes.map(c => (
                                <div className="bv-op is-ok" key={c.conexaoPlataformaId}>
                                    <span className={`bv-bx ${c.plataforma === PlataformaEnum.Instagram ? 'is-ig' : 'is-wa'}`} aria-hidden="true">
                                        {c.plataforma === PlataformaEnum.Instagram ? <FaInstagram size={18} /> : <FaWhatsapp size={18} />}
                                    </span>
                                    <span className="bv-tx">
                                        <span className="bv-nm">
                                            {rotuloDoCanal(c)}
                                            <span className="bv-selo is-ok"><LuCheck size={11} />Conectado</span>
                                        </span>
                                        <span className="bv-ap">{apoioDoCanal(c)}</span>
                                    </span>
                                    <span />
                                </div>
                            ))}

                            {!temWhatsapp && (
                                <div className="bv-op">
                                    <span className="bv-bx is-wa" aria-hidden="true"><FaWhatsapp size={18} /></span>
                                    <span className="bv-tx">
                                        <span className="bv-nm">WhatsApp · API oficial <span className="bv-selo">Recomendado</span></span>
                                        <span className="bv-ap">
                                            Funciona com o celular desligado ·{' '}
                                            <button type="button" className="bv-ap-lk" onClick={() => setPainelMeta(true)}>O que você vai precisar</button>
                                        </span>
                                    </span>
                                    <button type="button" className="bv-bt is-pri" onClick={conectarMeta}
                                        disabled={!metaPronto || conectandoMeta || !!bloqueioWa}
                                        title={bloqueioWa ?? (!metaPronto ? 'Carregando a configuração da Meta…' : undefined)}>
                                        {conectandoMeta ? 'Conectando…' : 'Conectar'}
                                    </button>
                                </div>
                            )}

                            {mostrarQr && !temWhatsapp && (
                                <div className="bv-op">
                                    <span className="bv-bx" aria-hidden="true"><LuQrCode size={18} /></span>
                                    <span className="bv-tx">
                                        <span className="bv-nm">WhatsApp · via QR Code</span>
                                        <span className="bv-ap">Como o WhatsApp Web; o celular fica ligado</span>
                                    </span>
                                    <button type="button" className="bv-bt" onClick={() => setModoQr(true)}
                                        disabled={!!bloqueioWa} title={bloqueioWa ?? undefined}>
                                        Ler QR Code
                                    </button>
                                </div>
                            )}

                            {mostrarInstagram && !temInstagram && (
                                <div className="bv-op">
                                    <span className="bv-bx is-ig" aria-hidden="true"><FaInstagram size={18} /></span>
                                    <span className="bv-tx">
                                        <span className="bv-nm">Instagram</span>
                                        <span className="bv-ap">Conta profissional do Instagram</span>
                                    </span>
                                    <button type="button" className="bv-bt" onClick={abrirInstagram} disabled={conectandoIg}>
                                        {conectandoIg ? 'Conectando…' : 'Conectar'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {celular && qrLigado && !temWhatsapp && (
                            <p className="bv-rod bv-nota">
                                <LuMonitor size={15} />
                                <span>Para conectar <b>via QR Code</b>, abra o Brik no computador.</span>
                            </p>
                        )}

                        <div className="bv-acoes">
                            {temCanal ? (
                                <button type="button" className="bv-btn" autoFocus onClick={() => setPasso(3)}>
                                    Continuar <LuArrowRight size={15} />
                                </button>
                            ) : (
                                <button type="button" className="bv-lk" onClick={() => setPasso(3)}>Conectar depois</button>
                            )}
                        </div>
                    </>
                )}

                {/* ─── 2 · lendo o QR Code ──────────────────────────────── */}
                {passo === 2 && modoQr && (
                    <>
                        <h1 className="bv-h1">Leia o código com o WhatsApp</h1>
                        <p className="bv-sub">No celular do número que vai atender.</p>

                        <div className="bv-qr">
                            <div className="bv-qr-cod">
                                {qr.qr ? (
                                    <img src={qr.qr} alt="QR Code para conectar o WhatsApp" width={184} height={184} />
                                ) : erroQr ? (
                                    <div className="bv-qr-erro"><LuTriangleAlert size={22} /><span>{qr.msg}</span></div>
                                ) : (
                                    <div className="bv-qr-skel" aria-label="Gerando o código…" />
                                )}
                            </div>
                            <div>
                                <ol className="bv-qr-passos">
                                    <li>Abra o <b>WhatsApp</b> no celular</li>
                                    <li>Toque em <b>Dispositivos conectados</b> › Conectar dispositivo</li>
                                    <li>Aponte a câmera para o código</li>
                                </ol>
                                {qr.fase === 'lendo' && <p className="bv-qr-tm"><i />Vale por 2 minutos · renovado sozinho até parear</p>}
                                {(qr.fase === 'criando' || qr.fase === 'gerando') && <p className="bv-qr-tm"><i />Gerando o código…</p>}
                                {erroQr && (
                                    <div className="bv-qr-acts">
                                        {qr.fase === 'recriar' ? (
                                            <button type="button" className="bv-bt is-pri" onClick={qr.recriar}><LuRefreshCw size={13} /> Recriar instância</button>
                                        ) : qr.fase === 'nome' ? (
                                            <button type="button" className="bv-bt is-pri" onClick={() => { pediuCriar.current = true; void qr.criar(NOME_DA_CONEXAO_QR); }}>
                                                <LuRefreshCw size={13} /> Tentar de novo
                                            </button>
                                        ) : (
                                            <button type="button" className="bv-bt is-pri" onClick={qr.tentarDeNovo} disabled={qr.ocupado}>
                                                <LuRefreshCw size={13} /> Gerar novo código
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bv-acoes">
                            <button type="button" className="bv-lk" onClick={() => setModoQr(false)}>Voltar</button>
                        </div>
                    </>
                )}

                {/* ─── 3 · Seu Brik está no ar ──────────────────────────── */}
                {passo === 3 && (
                    <>
                        <CenaBoasVindas passo={3} />
                        <h1 className="bv-h1">Seu Brik está no ar</h1>
                        <p className="bv-sub">
                            {temCanal
                                ? <>As conversas do <b>{rotuloDoCanal(conexoes[0]).replace(/^\w+ · /, '')}</b> já chegam no Chat.</>
                                : <>Quando um canal estiver conectado, as conversas chegam no Chat.</>}
                        </p>

                        <div className="bv-ops">
                            {temCanal
                                ? linhaDepois(<LuPackage size={18} />, 'Cadastre o que você vende', 'É o que o agente de IA vai oferecer', '/products')
                                : linhaDepois(<LuPlug size={18} />, 'Conecte um canal', 'WhatsApp ou Instagram, em Conexões', '/connections')}
                            {linhaDepois(<LuBot size={18} />, 'Crie o agente de IA', 'Tom de voz e o que ele sabe da empresa', '/chat-rules')}
                            {linhaDepois(<LuUserPlus size={18} />, 'Convide o time', 'Cada pessoa com o seu acesso', '/users')}
                        </div>

                        <div className="bv-acoes">
                            <button type="button" className="bv-btn" autoFocus onClick={() => concluir()} disabled={saindo}>
                                Ir para o Brik <LuArrowRight size={15} />
                            </button>
                        </div>
                        <p className="bv-rod">Isso tudo fica em <b>Primeiros passos</b>, no menu.</p>
                    </>
                )}
            </div>

            <ConectarMetaPanel
                open={painelMeta}
                onClose={() => setPainelMeta(false)}
                onConectar={conectarMeta}
                conectando={conectandoMeta}
                pronto={metaPronto}
                uso={{ usado: usadasWa, limite: limiteWa }}
            />
        </AuthShell>
    );
};

export default BemVindoPage;
